import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { newPlans } from '@/data/planSlides';
import { useMultiplePlansPricing } from '@/hooks/usePlanPricing';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { determineUpfrontBilling } from '@/lib/upfrontBilling';
import { useClientContext, useClientResolved } from '@/App';

function PlanCardSkeleton() {
  return (
    <div className="flex flex-col w-full h-full">
      <div
        className="relative flex flex-col rounded-3xl border border-white/70 text-white w-full h-full overflow-hidden min-h-[680px]"
        style={{ background: 'linear-gradient(180deg, #23136E 0%, #3927A5 55%, #4E3BCC 100%)' }}
      >
        {/* Header */}
        <div
          className="flex w-full flex-col items-start justify-start px-4 pt-4 pb-3 border-b border-white/20 h-[132px]"
          style={{ background: '#1E1458' }}
        >
          <div className="h-2.5 w-8 rounded bg-white/20 animate-pulse" />
          <div className="mt-3 h-8 w-40 rounded-lg bg-white/20 animate-pulse" />
          <div className="mt-3 h-4 w-24 rounded bg-white/10 animate-pulse" />
        </div>

        {/* Bullets */}
        <div className="flex flex-col px-4 pt-4 pb-2 h-[220px]">
          <ul className="space-y-4 w-full">
            {[72, 56, 80, 64].map((w, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-white/20 animate-pulse flex-shrink-0" />
                <div className={`h-3 rounded bg-white/20 animate-pulse`} style={{ width: `${w}%` }} />
              </li>
            ))}
          </ul>
          <div className="mt-auto border-t border-white/40" />
        </div>

        {/* Pricing */}
        <div className="px-3 pt-3 pb-3 space-y-4">
          <div className="flex items-start gap-3">
            <div className="mt-1.5 h-4 w-4 rounded-full bg-white/20 animate-pulse flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-8 w-36 rounded-lg bg-white/20 animate-pulse" />
              <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="mt-1.5 h-4 w-4 rounded-full bg-white/20 animate-pulse flex-shrink-0" />
            <div className="space-y-2 flex-1">
              <div className="h-8 w-32 rounded-lg bg-white/20 animate-pulse" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-auto px-3 pb-3">
          <div
            className="w-full rounded-xl bg-white/20 animate-pulse"
            style={{ height: '52px' }}
          />
        </div>
      </div>
    </div>
  );
}

const PlansSection = () => {
  const location = useLocation();
  const sectionRef = useRef<HTMLElement>(null);
  const [shouldLoadPricing, setShouldLoadPricing] = useState(false);
  const clientContext = useClientContext();
  const clientResolved = useClientResolved();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    if (location.pathname === '/plans') {
      setShouldLoadPricing(true);
      return;
    }

    if (!('IntersectionObserver' in window)) {
      setShouldLoadPricing(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadPricing(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 0px' },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [location.pathname]);

  // State to track selected billing interval for each plan
  const [selectedIntervals, setSelectedIntervals] = useState<Record<string, "monthly" | "annual">>({});
  const [creditBuilderOptIns, setCreditBuilderOptIns] = useState<Record<string, boolean>>({});
  const [showCreditModal, setShowCreditModal] = useState(false);

  // HC-3: surface a dismissible banner when /plans?error=plan_not_found
  // (Checkout.tsx redirects here on unknown / inactive carrier_plan_configs slug).
  const [searchParams, setSearchParams] = useSearchParams();
  const showPlanNotFoundBanner = searchParams.get('error') === 'plan_not_found';
  const dismissPlanNotFoundBanner = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('error');
    setSearchParams(next, { replace: true });
  };

  // Fetch pricing for all plans
  const planIds = newPlans.map(p => p.id);
  const { data: pricingMap, isLoading: isPricingLoading } = useMultiplePlansPricing(
    planIds,
    shouldLoadPricing,
  );

  const isLoading = isPricingLoading || !clientResolved;

  // Get eyebrow label based on plan ID
  const getEyebrowLabel = (id: string) => {
    switch (id) {
      // case '1971': return 'WiFi Always';
      // case '1974': return 'Wifi Mostly';
      // case '2055': return 'Wifi Sometimes';
      // case '2054': return 'Unlimited';
      // case '1963': return 'Perch Wearable';
      default: return '';
    }
  };

  const getDisplayTitle = (id: string, title: string) => {
    if (id === '2054') {
      return (
        <span className="inline-flex items-center gap-2">
          Truly <span className="text-[#B0F18D]">Unlimited</span>
          <span className="group relative inline-flex">
            <span
              aria-label="See terms for more details"
              className="text-sm font-medium leading-none text-white/80 cursor-help"
            >
              ⓘ
            </span>
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 top-full z-20 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-black/90 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity duration-150 group-hover:opacity-100"
            >
              See terms for more details
            </span>
          </span>
        </span>
      );
    }
    if (id === '1963') return 'Wearable 1G';
    if(id === '1974') return (
      <>
      Smart <span className="text-[#B0F18D]">Five</span>
      </>
    );
    if(id === '1971') return (
      <>
      Smart <span className="text-[#B0F18D]">One</span>
      </>
    );
    return title;
    
  };

  return (
    <section ref={sectionRef} className="relative w-full overflow-visible bg-primary py-10 lg:py-16">
      <div className="container relative mx-auto px-4">
        {showPlanNotFoundBanner && (
          <div
            role="status"
            aria-live="polite"
            className="mx-auto mb-6 flex items-start justify-between gap-3 rounded-lg border border-[#FFC300]/40 bg-[#FFC300]/10 px-4 py-3"
            style={{ maxWidth: '900px' }}
          >
            <p className="font-inter text-sm text-white/95 flex-1">
              That plan isn't available right now — pick another below.
            </p>
            <button
              type="button"
              onClick={dismissPlanNotFoundBanner}
              aria-label="Dismiss notification"
              className="shrink-0 text-white/70 hover:text-white text-xl leading-none px-1"
            >
              ×
            </button>
          </div>
        )}

        {/* Header */}
        <div className="mx-auto text-center" style={{ maxWidth: '1260px' }}>
          <h2 
            className="font-instrument font-semibold capitalize text-center text-foreground"
            style={{
              fontSize: 'clamp(28px, 5vw, 65px)',
              lineHeight: '1.15',
              letterSpacing: '-0.05em',
            }}
          >
            Plans That <span className="text-secondary">Make Sense.</span>
          </h2>
          
          <p 
            className="mx-auto mt-4 lg:mt-6 font-inter font-semibold capitalize text-center text-foreground"
            style={{
              maxWidth: '585px',
              fontSize: 'clamp(14px, 2vw, 25px)',
              lineHeight: '1.4',
              letterSpacing: '-0.05em',
            }}
          >
            No hidden fees, no surprise charges. Just straightforward pricing that helps you get ahead.
          </p>
        </div>

        {/* Plans Cards Container - Responsive Grid */}
        <div
          className="relative mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10 lg:mt-20"
          style={{
            maxWidth: '1200px',
          }}
        >
          {isLoading ? (
            <>
              <PlanCardSkeleton />
              <PlanCardSkeleton />
              <PlanCardSkeleton />
            </>
          ) : newPlans
            .filter(plan => pricingMap?.has(plan.id)) // Only show plans that are active in DB
            .map((plan) => {
            const showBadge = plan.id === '2054';
            const pricingData = pricingMap?.get(plan.id);
            const monthlyOption = pricingData?.monthly;
            const annualOption = pricingData?.annual;
            const hasAnnual = pricingData?.enabled_billing_intervals?.includes('annual') && annualOption;
            const upfront = determineUpfrontBilling(
              {
                upfront_billing_eligible: pricingData?.upfront_billing_eligible ?? false,
                retail_price: pricingData?.base_retail_price ?? 0,
                discounted_price: monthlyOption?.discounted_price ?? null,
                upfront_months: pricingData?.upfront_months ?? null,
              },
              clientContext,
            );
            // If the API doesn't return an annual price, force the selection to
            // monthly so the only visible option is also the one submitted to
            // the next step. Otherwise honor the user's selection (default annual).
            const userSelectedInterval = selectedIntervals[plan.id];
            const selectedInterval: "monthly" | "annual" = !hasAnnual
              ? 'monthly'
              : (userSelectedInterval ?? 'annual');
            
            return (
              <div
                key={plan.id}
                className="flex flex-col w-full h-full"
              >
                {/* Card */}
                <div
                  className="relative flex flex-col rounded-3xl border border-white/70 text-white w-full h-full overflow-hidden min-h-[680px]"
                  style={{
                    background: 'linear-gradient(180deg, #23136E 0%, #3927A5 55%, #4E3BCC 100%)',
                    zIndex: 10,
                  }}
                >
                  {/* Dark Header Section */}
                  <div 
                    className="flex w-full flex-col items-start justify-start px-4 pt-4 pb-3 border-b border-white/20 h-[132px]"
                    style={{
                      background: '#1E1458',
                    }}
                  >
                    <p className="text-white/80 text-xs font-medium">Perch</p>
                    <h3 className="mt-1 text-[38px] font-bold leading-[0.9] tracking-tight text-white">
                      {getDisplayTitle(plan.id, plan.title)}
                    </h3>
                    {showBadge && (
                      <span className="mt-2 inline-flex rounded-md bg-[#FFC300] px-2.5 py-1 text-[11px] font-semibold text-black">
                        Most Popular
                      </span>
                    )}
                    <p className="mt-2 text-xs text-white/70">{getEyebrowLabel(plan.id)}</p>
                  </div>

                  {/* Bullets */}
                  <div className="flex flex-col px-4 pt-4 pb-2 h-[220px]">
                    <ul className="space-y-3 w-full">
                      {plan.bullets.map((bullet, i) => (
                        <li key={i} className="flex items-start gap-3 font-inter text-white/95">
                          <span className="text-[#B0F18D] flex-shrink-0 text-sm pt-[2px]">✓</span>
                          <span className="text-sm leading-6">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-auto border-t border-white/40" />
                  </div>

                  {/* Pricing Section */}
                  <div className="px-3 pt-1 pb-3">
                    <RadioGroup
                      value={selectedInterval}
                      onValueChange={(value: string) => {
                        setSelectedIntervals(prev => ({
                          ...prev,
                          [plan.id]: value as "monthly" | "annual"
                        }));
                      }}
                      className="space-y-4"
                    >
                      {hasAnnual && (
                        <div
                          className="flex items-start gap-3 rounded-lg cursor-pointer"
                          onClick={() => {
                            setSelectedIntervals(prev => ({
                              ...prev,
                              [plan.id]: 'annual'
                            }));
                          }}
                        >
                          <RadioGroupItem
                            value="annual"
                            id={`${plan.id}-annual`}
                            className="mt-1.5 border-white/80 data-[state=checked]:border-[#B0F18D] data-[state=checked]:bg-[#B0F18D]"
                          />
                          <Label htmlFor={`${plan.id}-annual`} className="flex-1 cursor-pointer">
                            <div className="grid grid-cols-[1fr_auto] items-start gap-x-3">
                              <div className="min-w-0 pr-1">
                                <p className="text-[32px] font-bold leading-none text-white whitespace-nowrap">
                                  ${(annualOption?.discounted_price || annualOption?.retail_price || 0).toFixed(2)}
                                  <span className="text-xs font-medium text-white/90"> / month</span>
                                </p>
                                <p className="mt-1 text-xs leading-none font-semibold text-[#B0F18D]">Billed Yearly</p>
                              </div>
                              {annualOption?.discount_percent && (
                                <Badge className="pointer-events-none bg-[#FFC300] text-black rounded-md text-xs px-2 py-0.5 font-bold shrink-0 self-start mt-1 ml-1">
                                  - {annualOption.discount_percent}%
                                </Badge>
                              )}
                            </div>
                          </Label>
                        </div>
                      )}

                      <div
                        className="flex items-start gap-3 rounded-lg cursor-pointer"
                        onClick={() => {
                          setSelectedIntervals(prev => ({
                            ...prev,
                            [plan.id]: 'monthly'
                          }));
                        }}
                      >
                        <RadioGroupItem
                          value="monthly"
                          id={`${plan.id}-monthly`}
                          className="mt-1.5 border-white/70 data-[state=checked]:border-[#B0F18D] data-[state=checked]:bg-[#B0F18D]"
                        />
                        <Label htmlFor={`${plan.id}-monthly`} className="flex-1 cursor-pointer">
                          <p className="text-[32px] font-bold leading-none text-white whitespace-nowrap">
                            ${(monthlyOption?.discounted_price || monthlyOption?.retail_price || 0).toFixed(2)}
                            <span className="text-xs font-medium text-white/90"> / month</span>
                          </p>
                          {upfront.show && (
                            <p className="mt-1 text-xs font-semibold text-[#FFC300]">
                              {upfront.months} months upfront required — ${upfront.totalDue!.toFixed(2)} due today
                            </p>
                          )}
                        </Label>
                      </div>
                    </RadioGroup>

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/20">
                      <input
                        type="checkbox"
                        id={`${plan.id}-credit-builder`}
                        checked={creditBuilderOptIns[plan.id] || false}
                        onChange={(e) => setCreditBuilderOptIns(prev => ({ ...prev, [plan.id]: e.target.checked }))}
                        className="w-4 h-4 rounded accent-[#B0F18D] cursor-pointer"
                      />
                      <div className="flex items-center gap-1.5 flex-1">
                        <label
                          htmlFor={`${plan.id}-credit-builder`}
                          className="text-xs font-semibold text-white/90 cursor-pointer"
                        >
                          Add Credit Building
                        </label>
                        <span
                          onClick={(e) => { e.stopPropagation(); setShowCreditModal(true); }}
                          className="cursor-pointer text-white/60 hover:text-white/90 text-sm"
                          aria-label="Learn how credit building works"
                        >
                          ⓘ
                        </span>
                      </div>
                    </div>

                  </div>

                  {/* CTA Button pinned at card bottom */}
                  <div className="mt-auto px-3 pb-3">
                    <Link
                      to={`/plan-summary?plan=${plan.id}&interval=${selectedInterval}&creditBuilder=${creditBuilderOptIns[plan.id] || false}`}
                      className="flex items-center justify-center bg-[#9BE27E] transition-opacity hover:opacity-90 w-full text-black font-semibold text-base leading-none"
                      style={{
                        height: '52px',
                        borderRadius: '12px',
                      }}
                    >
                      Start Building
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showCreditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={() => setShowCreditModal(false)}>
          <div
            className="relative w-full max-w-sm rounded-2xl border border-white/20 p-6 text-white"
            style={{ background: '#1E1458' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="absolute right-4 top-4 text-white/60 hover:text-white text-lg leading-none" onClick={() => setShowCreditModal(false)}>✕</button>

            <h3 className="text-lg font-bold mb-3" style={{ color: '#B0F18D' }}>How Credit Building Works</h3>

            <p className="text-white font-semibold text-sm mb-3">Build your credit over time.</p>

            <p className="text-white/90 text-sm mb-3">Customers who opt in may become eligible for Perch's Credit Building program after <strong className="text-white">6 consecutive on-time monthly payments.</strong></p>

            <p className="text-white/90 text-sm mb-4">Once eligible, we'll contact you with next steps.</p>

            <p className="text-white font-bold text-sm">No credit check required to opt in.</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default PlansSection;
